import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import PhotonSuggestInput, { PhotonSuggestion } from './PhotonSuggestInput';
import FormRow, { compactCls, fieldCls } from './FormRow';
import LORDICONS from '../lordicons';

export type AddressFieldsValue = {
  street: string;
  country: string;
  region: string;
  city: string;
  zip: string;
};

type Props = {
  value: AddressFieldsValue;
  onChange: (value: AddressFieldsValue) => void;
  /** Photon autocomplete on street. Default true. Off for Settings → General (ARES). */
  enablePhoton?: boolean;
  disabled?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  /** Slide-over FormRow layout (icon + label + control) */
  variant?: 'stacked' | 'formRow';
  /** Mark street / city / zip / country as required (formRow) */
  requiredFields?: boolean;
};

const defaultInputCls =
  'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50';

const AddressFields: React.FC<Props> = ({
  value,
  onChange,
  enablePhoton = true,
  disabled = false,
  labelClassName = 'block text-xs font-medium text-gray-700 mb-1.5',
  inputClassName = defaultInputCls,
  variant = 'stacked',
  requiredFields = false,
}) => {
  const { t } = useLanguage();

  const set = (key: keyof AddressFieldsValue, v: string) =>
    onChange({ ...value, [key]: v });

  const applyPhoton = (s: PhotonSuggestion) => {
    onChange({
      street: s.street || value.street,
      country: s.country || value.country,
      region: s.region || value.region,
      city: s.city || value.city,
      zip: s.zip || value.zip,
    });
  };

  if (variant === 'formRow') {
    const controlCls = fieldCls;
    return (
      <div>
        <FormRow
          layout="stacked"
          icon={LORDICONS.pin}
          label={t('address.street')}
          required={requiredFields}
        >
          {enablePhoton && !disabled ? (
            <PhotonSuggestInput
              id="address-street"
              label={t('address.street')}
              value={value.street}
              onChange={(v) => set('street', v)}
              onSelect={applyPhoton}
              placeholder="Náměstí Svobody 1"
              disabled={disabled}
              hideLabel
              inputClassName={controlCls}
            />
          ) : (
            <input
              type="text"
              value={value.street}
              disabled={disabled}
              onChange={(e) => set('street', e.target.value)}
              className={controlCls}
              placeholder="Náměstí Svobody 1"
              aria-label={t('address.street')}
              required={requiredFields}
            />
          )}
        </FormRow>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <FormRow
            layout="stacked"
            icon={LORDICONS.building}
            label={t('address.city')}
            required={requiredFields}
          >
            <input
              type="text"
              value={value.city}
              disabled={disabled}
              onChange={(e) => set('city', e.target.value)}
              className={controlCls}
              placeholder="Brno"
              aria-label={t('address.city')}
              required={requiredFields}
            />
          </FormRow>
          <FormRow
            layout="stacked"
            icon={LORDICONS.document}
            label={t('address.zip')}
            required={requiredFields}
          >
            <input
              type="text"
              value={value.zip}
              disabled={disabled}
              onChange={(e) => set('zip', e.target.value)}
              className={`${compactCls} w-full`}
              placeholder="602 00"
              aria-label={t('address.zip')}
              required={requiredFields}
            />
          </FormRow>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <FormRow layout="stacked" icon={LORDICONS.location} label={t('address.region')}>
            <input
              type="text"
              value={value.region}
              disabled={disabled}
              onChange={(e) => set('region', e.target.value)}
              className={controlCls}
              placeholder="Jihomoravský kraj"
              aria-label={t('address.region')}
            />
          </FormRow>
          <FormRow
            layout="stacked"
            icon={LORDICONS.home}
            label={t('address.country')}
            required={requiredFields}
          >
            <input
              type="text"
              value={value.country}
              disabled={disabled}
              onChange={(e) => set('country', e.target.value)}
              className={controlCls}
              placeholder="Česká republika"
              aria-label={t('address.country')}
              required={requiredFields}
            />
          </FormRow>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {enablePhoton && !disabled ? (
        <PhotonSuggestInput
          id="address-street"
          label={t('address.street')}
          value={value.street}
          onChange={(v) => set('street', v)}
          onSelect={applyPhoton}
          placeholder="Náměstí Svobody 1"
          disabled={disabled}
          labelClassName={labelClassName}
          inputClassName={inputClassName}
        />
      ) : (
        <div>
          <label className={labelClassName}>{t('address.street')}</label>
          <input
            type="text"
            value={value.street}
            disabled={disabled}
            onChange={(e) => set('street', e.target.value)}
            className={inputClassName}
            placeholder="Náměstí Svobody 1"
          />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelClassName}>{t('address.city')}</label>
          <input
            type="text"
            value={value.city}
            disabled={disabled}
            onChange={(e) => set('city', e.target.value)}
            className={inputClassName}
            placeholder="Brno"
          />
        </div>
        <div>
          <label className={labelClassName}>{t('address.zip')}</label>
          <input
            type="text"
            value={value.zip}
            disabled={disabled}
            onChange={(e) => set('zip', e.target.value)}
            className={inputClassName}
            placeholder="602 00"
          />
        </div>
        <div>
          <label className={labelClassName}>{t('address.region')}</label>
          <input
            type="text"
            value={value.region}
            disabled={disabled}
            onChange={(e) => set('region', e.target.value)}
            className={inputClassName}
            placeholder="Jihomoravský kraj"
          />
        </div>
        <div>
          <label className={labelClassName}>{t('address.country')}</label>
          <input
            type="text"
            value={value.country}
            disabled={disabled}
            onChange={(e) => set('country', e.target.value)}
            className={inputClassName}
            placeholder="Česká republika"
          />
        </div>
      </div>
    </div>
  );
};

export default AddressFields;
